/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Modal contextuel de contrôle des accès au Mode IA :
 *  - Cas A : Utilisateur non authentifié (invitation à se connecter / créer un compte)
 *  - Cas B : Utilisateur authentifié sans privilège IA (offre réservée & contacts commerciaux)
 */

import React from 'react';
import { X, Sparkles, Lock, ShieldAlert, Phone, Mail, ArrowRight } from 'lucide-react';
import Settings from '../../utils/Settings';

interface AiAccessDeniedModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode?: 'IN_DEVELOPMENT' | 'UNAUTHENTICATED' | 'NO_PRIVILEGE';
  onLogin?: () => void;
}

export const AiAccessDeniedModal: React.FC<AiAccessDeniedModalProps> = ({
  isOpen,
  onClose,
  mode = 'IN_DEVELOPMENT',
  onLogin,
}) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-zinc-950/60 backdrop-blur-xs animate-fade-in"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md bg-white rounded-3xl p-6 sm:p-7 shadow-2xl border border-zinc-200/80 space-y-5 animate-scale-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-full transition cursor-pointer"
          aria-label="Fermer la fenêtre"
        >
          <X className="w-5 h-5" />
        </button>

        {mode === 'IN_DEVELOPMENT' ? (
          /* CAS EN COURS DE DÉVELOPPEMENT */
          <div className="space-y-4 text-center sm:text-left">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-[#1C0132] via-[#2A064B] to-[#3D0F73] text-amber-300 flex items-center justify-center shadow-md">
                <Sparkles className="w-6 h-6 animate-pulse" />
              </div>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-violet-50 text-primary-750 border border-violet-200/80">
                <span className="w-2 h-2 rounded-full bg-violet-600 animate-ping" />
                Bientôt disponible
              </span>
            </div>

            <div className="space-y-1.5">
              <h3 className="font-display text-lg sm:text-xl font-extrabold text-zinc-900">
                Assistant Juridique IA
              </h3>
              <p className="text-xs sm:text-sm text-zinc-600 leading-relaxed">
                Cette fonctionnalité d'analyse et de recherche juridique par Intelligence Artificielle est <strong>actuellement en cours de développement</strong>.
              </p>
            </div>

            {/* Aperçu des capacités futures */}
            <div className="bg-zinc-50 border border-zinc-200/80 rounded-2xl p-4 text-xs space-y-2 text-zinc-700">
              <span className="font-bold text-zinc-900 text-[11px] uppercase tracking-wider block">
                Ce que permettra l'assistant :
              </span>
              <ul className="space-y-1.5 text-[11px] leading-relaxed">
                <li className="flex items-start gap-2">
                  <span className="text-primary-750 font-bold">✓</span>
                  <span>Analyse automatique et qualification juridique de vos situations de fait</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary-750 font-bold">✓</span>
                  <span>Identification ciblée des lois, décrets et jurisprudences applicables</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary-750 font-bold">✓</span>
                  <span>Élaboration de mémorandums juridiques documentés et vérifiables</span>
                </li>
              </ul>
            </div>

            {/* Commercial contact box */}
            {(Settings.phone || Settings.email) && (
              <div className="bg-violet-50/50 border border-violet-100 rounded-2xl p-3.5 text-xs space-y-2 text-left">
                <span className="font-bold text-primary-950 text-[10px] uppercase tracking-wider block">
                  Des questions ou envie de tester la version bêta ?
                </span>
                {Settings.phone && (
                  <div className="flex items-center gap-2 text-zinc-700 text-[11px]">
                    <Phone className="w-3.5 h-3.5 text-primary-750 shrink-0" />
                    <span className="font-semibold">{Settings.phone}</span>
                  </div>
                )}
                {Settings.email && (
                  <div className="flex items-center gap-2 text-zinc-700 text-[11px]">
                    <Mail className="w-3.5 h-3.5 text-primary-750 shrink-0" />
                    <span className="font-semibold">{Settings.email}</span>
                  </div>
                )}
              </div>
            )}

            <div className="pt-1">
              <button
                type="button"
                onClick={onClose}
                className="w-full py-2.5 px-4 bg-primary-750 hover:bg-primary-850 text-white font-bold text-xs uppercase tracking-wider rounded-xl transition cursor-pointer border-none shadow-md shadow-primary-750/20"
              >
                Compris
              </button>
            </div>
          </div>
        ) : mode === 'UNAUTHENTICATED' ? (
          /* CAS A : NON CONNECTÉ */
          <div className="space-y-4 text-center sm:text-left">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-[#1C0132] to-[#3D0F73] text-amber-300 flex items-center justify-center shadow-md mx-auto sm:mx-0">
              <Sparkles className="w-6 h-6" />
            </div>

            <div className="space-y-1.5">
              <h3 className="font-display text-lg sm:text-xl font-extrabold text-zinc-900">
                Découvrez l'Assistant Juridique IA
              </h3>
              <p className="text-xs sm:text-sm text-zinc-600 leading-relaxed">
                Connectez-vous à votre compte Docarn Juridique pour accéder à l'analyse intelligente et
                au recoupement automatique des textes de loi avec vos questions juridiques.
              </p>
            </div>

            <div className="pt-2 flex flex-col sm:flex-row items-center gap-2.5">
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onLogin?.();
                }}
                className="w-full sm:flex-1 py-2.5 px-4 bg-primary-750 hover:bg-primary-800 text-white font-bold text-xs uppercase tracking-wider rounded-xl transition shadow-md shadow-primary-750/20 cursor-pointer flex items-center justify-center gap-2 border-none"
              >
                <span>Se connecter</span>
                <ArrowRight className="w-4 h-4" />
              </button>

              <button
                type="button"
                onClick={() => {
                  onClose();
                  onLogin?.();
                }}
                className="w-full sm:w-auto py-2.5 px-4 bg-zinc-100 hover:bg-zinc-200 text-zinc-800 font-bold text-xs rounded-xl transition cursor-pointer border border-zinc-200"
              >
                Créer un compte
              </button>
            </div>
          </div>
        ) : (
          /* CAS B : CONNECTÉ SANS PRIVILÈGE */
          <div className="space-y-4 text-center sm:text-left">
            <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-600 border border-amber-200 flex items-center justify-center shadow-2xs mx-auto sm:mx-0">
              <ShieldAlert className="w-6 h-6" />
            </div>

            <div className="space-y-1.5">
              <h3 className="font-display text-lg sm:text-xl font-extrabold text-zinc-900">
                Assistant Juridique IA — Accès réservé
              </h3>
              <p className="text-xs sm:text-sm text-zinc-600 leading-relaxed">
                Cette fonctionnalité d'analyse intelligente n'est pas incluse dans les privilèges de votre formule actuelle.
                Découvrez les offres Docarn Juridique permettant d'accéder à l'assistance juridique par IA.
              </p>
            </div>

            {/* Commercial contact box */}
            <div className="bg-zinc-50 border border-zinc-200/80 rounded-2xl p-4 text-xs space-y-2.5 text-left">
              <span className="font-bold text-zinc-800 text-[11px] uppercase tracking-wider block">
                Contacter notre équipe commerciale
              </span>
              {Settings.phone && (
                <div className="flex items-center gap-2 text-zinc-700">
                  <Phone className="w-3.5 h-3.5 text-primary-750 shrink-0" />
                  <span className="font-semibold">{Settings.phone}</span>
                </div>
              )}
              {Settings.email && (
                <div className="flex items-center gap-2 text-zinc-700">
                  <Mail className="w-3.5 h-3.5 text-primary-750 shrink-0" />
                  <span className="font-semibold">{Settings.email}</span>
                </div>
              )}
            </div>

            <div className="pt-1">
              <button
                type="button"
                onClick={onClose}
                className="w-full py-2.5 px-4 bg-zinc-900 hover:bg-zinc-800 text-white font-bold text-xs uppercase tracking-wider rounded-xl transition cursor-pointer border-none"
              >
                Compris
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AiAccessDeniedModal;
